import { CarouselItem } from '@/components/ui/carousel'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useTranslation } from 'react-i18next'
import { useNeedsAnalysis } from '@/contexts/needs-analysis/NeedsAnalysisContext'
import type { PrimaryGoal } from '@/types/learner-portal'
import { PRIMARY_GOALS } from '@/constants/needsAnalysis'

export default function PrimaryGoalsScreen() {
  const { t } = useTranslation()
  const { primary_goals, setGoals } = useNeedsAnalysis()
  const maxSelections = 2
  const canSelectMore = primary_goals.length < maxSelections

  const handleToggle = (goal: PrimaryGoal) => {
    const newGoals = primary_goals.includes(goal) ? primary_goals.filter((g) => g !== goal) : [...primary_goals, goal]

    if (newGoals.length <= maxSelections) {
      setGoals(newGoals)
    }
  }

  return (
    <CarouselItem className='px-2 sm:px-8'>
      <div className='space-y-2 py-2 sm:space-y-6 sm:py-6'>
        <div className='space-y-1 sm:space-y-2'>
          <h3 id='primary-goals-heading' className='text-base font-medium text-gray-900 sm:text-xl'>
            {t('needsAnalysis.primaryGoals.title')}
          </h3>
          <p className='text-sm text-gray-700 sm:text-lg'>{t('needsAnalysis.primaryGoals.subtitle')}</p>
          <p id='primary-goals-hint' className='text-xs text-gray-600 sm:text-sm'>
            {t('needsAnalysis.primaryGoals.selectUpTo')}
          </p>
        </div>

        <div
          role='group'
          aria-labelledby='primary-goals-heading'
          aria-describedby='primary-goals-hint'
          className='max-h-[calc(90vh-280px)] space-y-2 overflow-y-auto pr-1 sm:max-h-[240px] sm:space-y-3 sm:pr-2'
        >
          {PRIMARY_GOALS.map((goal) => {
            const isSelected = primary_goals.includes(goal)
            const isDisabled = !isSelected && !canSelectMore

            return (
              <Label
                key={goal}
                htmlFor={`goal-${goal}`}
                className={`flex cursor-pointer items-start space-x-2 rounded-lg border border-gray-200 p-2 transition-colors sm:space-x-3 sm:p-4 ${
                  isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50'
                } ${isSelected ? 'border-blue-500 bg-blue-50' : ''}`}
              >
                <Checkbox
                  id={`goal-${goal}`}
                  checked={isSelected}
                  onCheckedChange={() => !isDisabled && handleToggle(goal)}
                  disabled={isDisabled}
                  className='mt-0.5 sm:mt-1'
                />
                <span className='flex-1 text-sm font-medium sm:text-base'>
                  {t(`needsAnalysis.primaryGoals.goals.${goal}`)}
                </span>
              </Label>
            )
          })}
        </div>

        <p role='status' aria-live='polite' className='text-xs text-gray-500 sm:text-sm'>
          {primary_goals.length === maxSelections ? t('needsAnalysis.primaryGoals.maxSelectionsMessage') : ''}
        </p>
      </div>
    </CarouselItem>
  )
}
